import React, { FC, useCallback, useMemo, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { message } from 'antd';
import { metersRestCrudThunksForSupplier } from '../../thunks/meters_thunks';
import { meterReadingsRestCrudThunksForSupplier } from '../../thunks/meter_readings_thunks';
import { EmptyState } from '../empty_state/EmptyState';
import { METERS_CRUD_ACTION_CREATORS } from '../../actions/meters_actions';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { useFilters } from '../../hooks/useFilters';
import { getMeterFilterConfig } from '../../utils/MeterFilterUtils';
import SupplierMeterReadingModalForm from '../supplier_meter_reading_modal_form/SupplierMeterReadingModalForm';
import DateTimeHover from '../date_time_component/DateTime';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import MeterRowActions from '../meter_row_actions/MeterRowActions';

require('./SupplierMetersIndexPage.less');

const DEFAULT_METERS_TARGET_COLLECTION = 'metersIndex';
const METER_ADVANCED_FILTERS = 'metersAdvancedFilters';

const SupplierMetersIndexPage: FC<any> = ({
	history,
	location,
	meters,
	updateFilters,
	updateSorters,
	clearAndUpdateFilters,
	createMeterReading,
	fetchMeters,
	archiveMeter,
	unarchiveMeter,
	preAppliedFilters = {},
	targetCollectionName = DEFAULT_METERS_TARGET_COLLECTION,
}) => {
	const [meterReadingModalVisible, setMeterReadingModalVisible] = useState(false);
	const [selectedMeter, setSelectedMeter] = useState<any>(null);
	const formRef = useRef<any>(null);

	const getStateSliceProp = useCallback(
		(propName) => nullSafeGetOrElse(`${targetCollectionName}.${propName}`, meters, {}),
		[meters, targetCollectionName]
	);

	const refreshMeters = useCallback(
		() =>
			fetchMeters(
				{},
				targetCollectionName,
				getStateSliceProp('pagination'),
				getStateSliceProp('sorting'),
				getStateSliceProp('filters')
			),
		[fetchMeters, getStateSliceProp, targetCollectionName]
	);

	const handleAddReadingClick = useCallback((e, record) => {
		e.stopPropagation();
		setSelectedMeter(record);
		setMeterReadingModalVisible(true);
	}, []);

	// archive/unarchive show their own toast with an Undo window and defer the write, so we
	// don't emit a success message here; refresh once the write actually lands.
	const handleArchiveMeter = useCallback(
		(meter) => archiveMeter(meter).then(() => refreshMeters()),
		[archiveMeter, refreshMeters]
	);

	const handleUnarchiveMeter = useCallback(
		(meter) => unarchiveMeter(meter).then(() => refreshMeters()),
		[unarchiveMeter, refreshMeters]
	);

	const handleModalCancel = useCallback(() => {
		if (formRef.current) {
			const form = formRef.current.props.form;
			form.resetFields();
		}
		setMeterReadingModalVisible(false);
		setSelectedMeter(null);
	}, []);

	const handleModalSubmit = useCallback(() => {
		if (!formRef.current) return;
		const form = formRef.current.props.form;
		form.validateFields((err, values) => {
			if (err) return;
			createMeterReading(values).then(() => {
				setMeterReadingModalVisible(false);
				setSelectedMeter(null);
				message.success('Meter reading recorded.');
				form.resetFields();
				refreshMeters();
			});
		});
	}, [createMeterReading, refreshMeters]);

	const onRow = useCallback(
		(record) => ({
			onClick: () => {
				let searchString = location.search;
				if (searchString[0] === '?') {
					searchString = searchString.slice(1);
				}
				// Carry the originating overview tab so returning from details lands on the
				// same tab (all/active/archived) rather than always "all".
				const params = [`backTab=${encodeURIComponent(location.pathname)}`];
				if (searchString && searchString.length > 0) {
					params.push(`backlinkParams=${encodeURIComponent(searchString)}`);
				}
				history.push(`/supplier/meters/detail/${record.id}/details?${params.join('&')}`);
			},
		}),
		[history, location]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Location',
				dataIndex: ['location', 'name'],
				sorter: true,
			},
			{
				title: 'Asset',
				dataIndex: ['asset', 'name'],
				sorter: true,
			},
			{
				title: 'Meter Name',
				dataIndex: 'name',
				sorter: true,
			},
			{
				title: 'Last Reading',
				dataIndex: ['lastReading', 'value'],
				render: (text, record) => {
					const value = nullSafeGet('lastReading.value', record);
					const unit = nullSafeGet('valueUnit', record);
					if (value == null) return '--';
					return (
						<div>
							<div>{value + (unit ? ' ' + unit : '')}</div>
							{nullSafeGet('lastReading.recordedAt', record) && (
								<div style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.45)' }}>
									<DateTimeHover
										timestamp={nullSafeGet('lastReading.recordedAt', record)}
										timeZone={nullSafeGet('location.timeZoneId', record)}
									/>
								</div>
							)}
						</div>
					);
				},
			},
			{
				title: '',
				dataIndex: 'id',
				render: (text, record) => (
					<MeterRowActions
						record={record}
						onAddReadingClick={handleAddReadingClick}
						onArchive={handleArchiveMeter}
						onUnarchive={handleUnarchiveMeter}
					/>
				),
			},
		],
		[handleAddReadingClick, handleArchiveMeter, handleUnarchiveMeter]
	);

	const updateFiltersForCollection = useCallback(
		(filters) => updateFilters(filters, targetCollectionName),
		[updateFilters, targetCollectionName]
	);

	const allFilters = useFilters({
		filterTargetCollectionName: METER_ADVANCED_FILTERS,
		overviewCollectionName: targetCollectionName,
		updateFilters: updateFiltersForCollection,
	});

	const filterConfig = useMemo(() => getMeterFilterConfig({ filters: allFilters }), [allFilters]);

	return (
		<>
			{selectedMeter && (
				<SupplierMeterReadingModalForm
					isEdit={false}
					meter={selectedMeter}
					formData={{ meterId: selectedMeter.id }}
					wrappedComponentRef={(ref) => {
						formRef.current = ref;
					}}
					visible={meterReadingModalVisible}
					onCancel={handleModalCancel}
					onSubmit={handleModalSubmit}
				/>
			)}
			<PaginatedReduxTableWithHeader
				targetCollectionName={targetCollectionName}
				stateSlice={meters}
				updateFilters={updateFilters}
				updateSorters={updateSorters}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={filterConfig}
				entityCollectionName="meters"
				tableColumns={columns}
				showHeader={true}
				fetchData={metersRestCrudThunksForSupplier.read}
				onTableRow={onRow}
				keyAccessor={(el) => el.id}
				initialPagination={{ current: 1, pageSize: 10 }}
				preAppliedFilters={preAppliedFilters}
				rightActions={<div></div>}
				emptyState={
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
								alt="You haven't created any meters yet."
							/>
						}
						headline={"You haven't created any meters yet."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}></div>
							</div>
						}
					/>
				}
			/>
		</>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	meters: state.meters,
});

const mapDispatchToProps = (dispatch) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(METERS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	updateSorters: (sortBy, order, targetCollection) =>
		dispatch(METERS_CRUD_ACTION_CREATORS.updateSorting(sortBy, order, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(METERS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)),
	createMeterReading: (meterReading) =>
		dispatch(meterReadingsRestCrudThunksForSupplier.create(meterReading)),
	fetchMeters: (params, targetCollectionName, pagination, sorting, filters) =>
		dispatch(
			metersRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters
			)
		),
	archiveMeter: (meter) => dispatch(metersRestCrudThunksForSupplier.archive(meter, 'id', true)),
	unarchiveMeter: (meter) => dispatch(metersRestCrudThunksForSupplier.unarchive(meter, 'id', true)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(SupplierMetersIndexPage)
);
